import Decimal from 'decimal.js';

Decimal.set({ precision: 30, rounding: Decimal.ROUND_HALF_EVEN });

// P2-2: Cents are integers (optionally signed for engine over-contrib
// arithmetic). Reject 'NaN', 'Infinity', '-Infinity', '', decimals, and
// any non-numeric string before Decimal.js silently produces $NaN /
// $-Infinity downstream (validation finding P2-2).
const CENTS_INT_RE = /^-?\d+$/;

export function centsToDecimal(centsString: string): Decimal {
  if (typeof centsString !== 'string' || !CENTS_INT_RE.test(centsString)) {
    throw new Error(`invalid cents string: ${JSON.stringify(centsString)}`);
  }
  return new Decimal(centsString);
}

export function decimalToCents(d: Decimal): string {
  return d.toFixed(0);
}

export function addCents(a: string, b: string): string {
  return decimalToCents(centsToDecimal(a).plus(centsToDecimal(b)));
}

export function formatCAD(centsString: string): string {
  const d = centsToDecimal(centsString);
  const dollars = d.dividedBy(100);
  const isWhole = d.modulo(100).equals(0);
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2
  });
  return formatter.format(dollars.toNumber()).replace('CA', '');
}

/**
 * v1.0.4 P0 (defect B): split a signed cents string into a sign character
 * and the unsigned formatted magnitude. Activity-row renderers must use
 * this instead of concatenating a hard-coded sign prefix with formatCAD —
 * a legacy bad record with amount_cents='-500000' would otherwise render
 * as "+-$5,000" (the prefix's "+" plus formatCAD's own "-").
 *
 * Sign convention (per hotfix spec): "+" iff amount > 0, else "−".
 * Zero amounts get "−" — engine + reducer floors make this case rare.
 */
export function signedCAD(centsString: string): { sign: '+' | '−'; text: string } {
  const cents = centsToDecimal(centsString);
  const sign = cents.greaterThan(0) ? '+' : '−';
  return { sign, text: formatCAD(cents.abs().toFixed(0)) };
}
